import React, { useEffect, useRef, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { GHGInventoryResult } from './GHGInventorySupabase';

Chart.register(...registerables);

type ChartType = 'stacked-area' | 'line' | 'pie';

interface SectorBasedGHGInventoryProps {
  location: string;
  chartType: ChartType;
  setChartType: (chartType: ChartType) => void;
  ghgData?: GHGInventoryResult | null;
}

export const SectorBasedGHGInventory: React.FC<SectorBasedGHGInventoryProps> = ({ location, chartType, setChartType, ghgData }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  // Debug: Track chart type changes
  useEffect(() => {
    console.log('Chart type state changed to:', chartType);
  }, [chartType]);

  // Use real data from props, fallback to sample data if not available
  const sampleData: ChartDataPoint[] = [
    { year: 2005, lightDutyVehicles: 410000, heavyDutyVehicles: 50000, naturalGasResidential: 83000, electricityCommercial: 48000, electricityResidential: 42000, naturalGasCommercial: 38000, waste: 18000, offroad: 15000 },
    { year: 2008, lightDutyVehicles: 395000, heavyDutyVehicles: 48000, naturalGasResidential: 81000, electricityCommercial: 46000, electricityResidential: 40000, naturalGasCommercial: 36000, waste: 17000, offroad: 14500 },
    { year: 2011, lightDutyVehicles: 375000, heavyDutyVehicles: 46000, naturalGasResidential: 78000, electricityCommercial: 44000, electricityResidential: 38000, naturalGasCommercial: 34000, waste: 16000, offroad: 14000 },
    { year: 2014, lightDutyVehicles: 355000, heavyDutyVehicles: 44000, naturalGasResidential: 75000, electricityCommercial: 42000, electricityResidential: 36000, naturalGasCommercial: 32000, waste: 16000, offroad: 13500 },
    { year: 2017, lightDutyVehicles: 335000, heavyDutyVehicles: 42000, naturalGasResidential: 72000, electricityCommercial: 40000, electricityResidential: 34000, naturalGasCommercial: 30000, waste: 15000, offroad: 13000 },
    { year: 2020, lightDutyVehicles: 315000, heavyDutyVehicles: 40000, naturalGasResidential: 69000, electricityCommercial: 38000, electricityResidential: 32000, naturalGasCommercial: 28000, waste: 15000, offroad: 12500 },
    { year: 2023, lightDutyVehicles: 295000, heavyDutyVehicles: 38000, naturalGasResidential: 66000, electricityCommercial: 36000, electricityResidential: 30000, naturalGasCommercial: 26000, waste: 15000, offroad: 12000 }
  ];

  // Define type for chart data points
  type ChartDataPoint = {
    year: number;
    lightDutyVehicles: number;
    heavyDutyVehicles: number;
    naturalGasResidential: number;
    electricityCommercial: number;
    electricityResidential: number;
    naturalGasCommercial: number;
    waste: number;
    offroad: number;
  };

  // Convert real data to chart format if available
  const chartData: ChartDataPoint[] = ghgData ? (() => {
    const years = ghgData.chartData.labels;
    const datasets = ghgData.chartData.datasets;
    
    return years.map((year: any, index: number) => {
      const dataPoint: ChartDataPoint = { 
        year: parseInt(year),
        lightDutyVehicles: 0,
        heavyDutyVehicles: 0,
        naturalGasResidential: 0,
        electricityCommercial: 0,
        electricityResidential: 0,
        naturalGasCommercial: 0,
        waste: 0,
        offroad: 0
      };
      datasets.forEach((dataset: any) => {
        const value = dataset.data[index] || 0;
        // Map dataset labels to our expected property names
        switch (dataset.label) {
          case 'Light Duty Vehicles':
            dataPoint.lightDutyVehicles = value;
            break;
          case 'Heavy Duty Vehicles':
            dataPoint.heavyDutyVehicles = value;
            break;
          case 'Natural Gas Residential':
            dataPoint.naturalGasResidential = value;
            break;
          case 'Electricity Commercial':
            dataPoint.electricityCommercial = value;
            break;
          case 'Electricity Residential':
            dataPoint.electricityResidential = value;
            break;
          case 'Natural Gas Commercial':
            dataPoint.naturalGasCommercial = value;
            break;
          case 'Waste':
            dataPoint.waste = value;
            break;
          case 'Offroad':
            dataPoint.offroad = value;
            break;
        }
      });
      return dataPoint;
    });
  })() : sampleData;

  const createChart = useCallback(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    // Clear the canvas
    if (ctx) {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }

    console.log('Creating chart with type:', chartType);
    console.log('Chart type check - isStacked:', chartType === 'stacked-area', 'isLineChart:', chartType === 'line', 'isPie:', chartType === 'pie');

    let chartConfig: any;

    if (chartType === 'pie') {
      // Pie chart for 2023 data only
      const pieData = chartData[chartData.length - 1]; // Latest year data
      chartConfig = {
        type: 'pie',
        data: {
          labels: [
            'Light Duty Vehicles',
            'Heavy Duty Vehicles',
            'Natural Gas - Residential',
            'Electricity - Commercial',
            'Electricity - Residential',
            'Natural Gas - Commercial',
            'Waste',
            'Offroad'
          ],
          datasets: [{
            data: [
              pieData.lightDutyVehicles,
              pieData.heavyDutyVehicles,
              pieData.naturalGasResidential,
              pieData.electricityCommercial,
              pieData.electricityResidential,
              pieData.naturalGasCommercial,
              pieData.waste,
              pieData.offroad
            ],
            backgroundColor: [
              '#3b82f6',  // Light Duty Vehicles - Transportation (blue)
              '#60a5fa',  // Heavy Duty Vehicles - Transportation (lighter blue)
              '#f59e0b',  // Natural Gas Residential - Natural Gas (amber)
              '#fbbf24',  // Natural Gas Commercial - Natural Gas (lighter amber)
              '#10b981',  // Electricity Residential - Electricity (emerald)
              '#34d399',  // Electricity Commercial - Electricity (lighter emerald)
              '#ec4899',  // Waste (pink)
              '#FFD200'   // Offroad (gold)
            ],
            borderColor: '#ffffff',
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 10,
              bottom: 10,
              left: 10,
              right: 10
            }
          },
          plugins: {
            title: {
              display: true,
              text: `Sector-Based GHG Inventory - ${location}`,
              font: {
                size: 16,
                weight: 'bold' as const
              },
              padding: {
                top: 10,
                bottom: 20
              }
            },
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 13
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context: any) {
                  const label = context.label || '';
                  const value = context.parsed;
                  const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${label}: ${value.toLocaleString()} MT CO₂e (${percentage}%)`;
                }
              }
            }
          }
        }
      };
    } else {
      // Line or stacked area chart for time series
      const isStacked = chartType === 'stacked-area';
      const isLineChart = chartType === 'line';
      
      console.log('Chart type:', chartType, 'isStacked:', isStacked, 'isLineChart:', isLineChart);
      
      chartConfig = {
        type: 'line', // Always use line type, control stacking via fill and stack properties
        data: {
          labels: chartData.map(d => d.year),
          datasets: [
            {
              label: 'Light Duty Vehicles',
              data: chartData.map(d => d.lightDutyVehicles),
              borderColor: 'rgba(59, 130, 246, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0.4,
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Heavy Duty Vehicles',
              data: chartData.map(d => d.heavyDutyVehicles),
              borderColor: 'rgba(96, 165, 250, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(96, 165, 250, 0.8)' : 'rgba(96, 165, 250, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0.4,
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Natural Gas - Residential',
              data: chartData.map(d => d.naturalGasResidential),
              borderColor: 'rgba(245, 158, 11, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(245, 158, 11, 0.8)' : 'rgba(245, 158, 11, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0.4,
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Natural Gas - Commercial',
              data: chartData.map(d => d.naturalGasCommercial),
              borderColor: 'rgba(251, 191, 36, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(251, 191, 36, 0.8)' : 'rgba(251, 191, 36, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0.4,
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Electricity - Residential',
              data: chartData.map(d => d.electricityResidential),
              borderColor: 'rgba(16, 185, 129, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(16, 185, 129, 0.8)' : 'rgba(16, 185, 129, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0.4,
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Electricity - Commercial',
              data: chartData.map(d => d.electricityCommercial),
              borderColor: 'rgba(52, 211, 153, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(52, 211, 153, 0.8)' : 'rgba(52, 211, 153, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0.4,
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Waste',
              data: chartData.map(d => d.waste),
              borderColor: 'rgba(236, 72, 153, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(236, 72, 153, 0.8)' : 'rgba(236, 72, 153, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0.4,
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Offroad',
              data: chartData.map(d => d.offroad),
              borderColor: 'rgba(255, 210, 0, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(255, 210, 0, 0.8)' : 'rgba(255, 210, 0, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0.4,
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 10,
              bottom: 10,
              left: 10,
              right: 10
            }
          },
          plugins: {
            title: {
              display: true,
              text: `Sector-Based GHG Inventory - ${location}`,
              font: {
                size: 16,
                weight: 'bold' as const
              },
              padding: {
                top: 10,
                bottom: 20
              }
            },
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 13
                }
              }
            }
          },
          scales: {
            x: {
              stacked: isStacked,
              title: {
                display: true,
                text: 'Year',
                font: {
                  size: 13,
                  weight: ('bold' as const)
                },
                padding: { top: 10, bottom: 5 }
              },
              ticks: {
                font: {
                  size: 12
                },
                maxRotation: 45,
                minRotation: 45
              },
              grid: {
                display: false
              }
            },
            y: {
              stacked: isStacked,
              title: {
                display: true,
                text: 'GHG Emissions (MT CO₂e)',
                font: {
                  size: 13,
                  weight: ('bold' as const)
                },
                padding: { top: 5, bottom: 10 }
              },
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              ticks: {
                font: {
                  size: 12
                }
              }
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        }
      };
    }

    // Create new chart instance
    chartInstance.current = new Chart(ctx, chartConfig);
  }, [chartType, location, chartData]);

  useEffect(() => {
    createChart();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartType, location, createChart]);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%',
      minHeight: '500px',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <canvas ref={chartRef} key={chartType} />
    </div>
  );
};
